import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { FormGroup, Label } from './styles/common';
import { reactSelectStyles } from './reactSelectStyles';
import { PlainCheckboxLabel } from './PlainCheckbox.styles';

function FormReactSelectInputBirthPlace({
  options, states, values, onChange, valid, error, label, name, valid_country_of_birth_code, error_country_of_birth_code,
}) {
  const [perpOptions, setPerpOptions] = useState([]);
  const [prepStates, setPrepStates] = useState([]);
  const [inputText, setInputText] = useState('');
  // "Stato estero" only becomes required the moment it is ticked, so hold back
  // its validation styling/message until the broker has actually engaged with
  // the state select (blur or change), or a value is already restored.
  const [countryTouched, setCountryTouched] = useState(false);
  const countryHasValue = Boolean(values.country_of_birth_code) && values.country_of_birth_code !== 'Z000';
  // ...or once the parent has surfaced an actual error for it (e.g. the broker
  // pressed "Continua" without ever opening the select).
  const showCountryState = countryTouched || countryHasValue || Boolean(error_country_of_birth_code);
  // Same story in reverse: unchecking "Stato estero" clears/re-requires the
  // comune field and touches it programmatically, so hold back its validation
  // styling/message until the broker has actually engaged with it too.
  const [communeTouched, setCommuneTouched] = useState(false);
  const communeHasValue = Boolean(values.commune_of_birth_code);
  const showCommuneState = communeTouched || communeHasValue || Boolean(error);

  const filterMunicipalities = (inputValue) => {
    if (inputValue.length >= 3) {
      return perpOptions.filter((i) => i.label.toLowerCase().startsWith(inputValue.toLowerCase()));
    }

    return [];
  };

  const promiseOptions = (inputValue) => new Promise((resolve) => {
    setTimeout(() => {
      resolve(filterMunicipalities(inputValue));
    }, 500);
  });

  const filterStates = (inputValue) => {
    if (inputValue.length >= 3) {
      return prepStates.filter((i) => i.label.toLowerCase().startsWith(inputValue.toLowerCase()));
    }

    return [];
  };

  const promiseOptionsStates = (inputValue) => new Promise((resolve) => {
    setTimeout(() => {
      resolve(filterStates(inputValue));
    }, 500);
  });

  const handleChange = (value) => {
    if (value.born_abroad !== undefined) {
      if (value.born_abroad) {
        onChange({
          born_abroad: value.born_abroad,
          commune_of_birth_code: null,
          province_of_birth_code: null,
        });
      } else {
        onChange({
          born_abroad: value.born_abroad,
          country_of_birth_code: 'Z000',
          province_of_birth_code: null,
          commune_of_birth_code: null,
        });
      }
      return;
    }

    if (!value.born_abroad) {
      if (value.commune_of_birth_code !== undefined || value.commune_of_birth_code !== '') {
        const commune = options.find((opt) => opt.cadastral_code === value.commune_of_birth_code);

        if (commune !== undefined) {
          onChange({
            commune_of_birth_code: commune.cadastral_code,
            province_of_birth_code: commune.communal_territory.car_plate_symbol,
            born_abroad: false,
            country_of_birth_code: 'Z000',
          });
          return;
        }
      }
    }

    if (value.commune_of_birth_code === null || value.commune_of_birth_code === '') {
      onChange({
        commune_of_birth_code: '',
        province_of_birth_code: '',
        born_abroad: false,
        country_of_birth_code: 'Z000',
      });
      return;
    }

    if (value.country_of_birth_code !== null || value.country_of_birth_code !== '' || value.country_of_birth_code !== undefined) {
      onChange({
        country_of_birth_code: value.country_of_birth_code,
        commune_of_birth_code: null,
        province_of_birth_code: null,
        born_abroad: true,
      });
    }
  };

  const handleInputChange = (inputValue) => {
    setInputText(inputValue);
  };

  const handleMenuClose = () => {
    if (!values.commune_of_birth_code && inputText) {
      const startingWithOptions = perpOptions.filter((option) => option.label.toLowerCase().startsWith(inputText.toLowerCase()));
      const firstStartingOption = startingWithOptions[0];
      const findCarPlateSymbol = options.find((opt) => opt.cadastral_code === firstStartingOption.value);
      if (firstStartingOption) {
        onChange({
          commune_of_birth_code: firstStartingOption.value,
          province_of_birth_code: findCarPlateSymbol.communal_territory.car_plate_symbol,
          born_abroad: false,
          country_of_birth_code: 'Z000',
        });
      }
    }
  };

  useEffect(() => {
    setPerpOptions(options.map((i) => ({ value: i.cadastral_code, label: `${i.name} (${i.communal_territory.car_plate_symbol})` })));
    setPrepStates(states.map((i) => ({ value: i.state_code, label: i.name })));
  }, [options]);

  return (
    <FormGroup>
      <div className="row">
        <div className="col-12 col-md-12">
          <Label>{label}</Label>
          <AsyncSelect
            // Toggling "Stato estero" clears commune_of_birth_code correctly
            // in form state, but react-select doesn't always re-sync its own
            // displayed selection across the isDisabled flip that comes with
            // it. Forcing a remount on that flip guarantees it starts fresh
            // from the current (correct) value instead of showing a stale one.
            key={values.born_abroad ? 'comune-abroad' : 'comune-domestic'}
            onMenuClose={handleMenuClose}
            onInputChange={handleInputChange}
            loadOptions={promiseOptions}
            defaultOptions
            isSearchable
            placeholder="Cerca il comune di nascita..."
            name={name}
            isDisabled={values.born_abroad === true}
            value={values.born_abroad === false ? options.filter((opt) => opt.cadastral_code === values.commune_of_birth_code).map((o) => ({ value: o.cadastral_code, label: `${o.name} (${o.communal_territory.car_plate_symbol})` }))[0] : ''}
            onBlur={() => setCommuneTouched(true)}
            onChange={(val) => {
              setCommuneTouched(true);
              handleChange({
                commune_of_birth_code: val === null || val === undefined ? '' : val.value,
              });
            }}
            loadingMessage={() => 'Caricando communi...'}
            clearValue
            isClearable
            noOptionsMessage={() => 'Digita almeno tre lettere del comune!'}
            styles={reactSelectStyles({
              error: !values.born_abroad && showCommuneState && Boolean(error),
              valid: !values.born_abroad && showCommuneState && valid,
            })}
          />
          {!values.born_abroad && showCommuneState && error
            && <div className="invalid-feedback d-block">{error}</div>}
          <div className="mt-2">
            <PlainCheckboxLabel>
              <input
                type="checkbox"
                name="born_abroad"
                checked={values.born_abroad}
                onChange={() => {
                  const checkingBox = !values.born_abroad;
                  setCountryTouched(false);
                  // Checking "Stato estero" disables and clears the comune
                  // field, but if it currently holds a real selection, that
                  // selection is about to be lost — remember it as touched so
                  // that once the broker unchecks the box again, the now-empty
                  // (and once again required) field reads as invalid right
                  // away instead of looking untouched. Checking while it was
                  // already empty keeps the original neutral behavior. On the
                  // uncheck itself, leave communeTouched as-is so that flag
                  // (set above) survives into the re-enabled, now-empty field.
                  if (checkingBox) setCommuneTouched(communeHasValue);
                  handleChange({ born_abroad: checkingBox });
                }}
              />
              Stato estero
            </PlainCheckboxLabel>
          </div>
          {values.born_abroad === true && states !== undefined
            && (
              <div className="mt-3">
                <AsyncSelect
                  loadOptions={promiseOptionsStates}
                  defaultOptions
                  isSearchable
                  isClearable
                  placeholder="Cerca lo stato..."
                  name="country_of_birth_code"
                  noOptionsMessage={() => 'Digita almeno tre lettere dello stato!'}
                  onBlur={() => setCountryTouched(true)}
                  onChange={(val) => {
                    setCountryTouched(true);
                    handleChange({
                      country_of_birth_code: val !== null ? val.value : '',
                    });
                  }}
                  value={states.filter((opt) => opt.state_code === values.country_of_birth_code).map((o) => ({ value: o.state_code, label: o.name }))[0]}
                  styles={reactSelectStyles({
                    error: showCountryState && Boolean(error_country_of_birth_code),
                    valid: showCountryState && Boolean(valid_country_of_birth_code),
                  })}
                />
                {showCountryState && error_country_of_birth_code
                  && <div className="invalid-feedback d-block">{error_country_of_birth_code}</div>}
              </div>
            )}
        </div>
      </div>
    </FormGroup>
  );
}

export default FormReactSelectInputBirthPlace;
